import {
    AttachedFiles,
    type AttachedFilesRequest,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import {
    type IHasLineItems,
    type ILineItemContainerData,
    type ILineItemData,
    type ILineItemResponse,
    LineItem,
    LineItemType,
    LinkedCostItemTypes,
    PriceTypes,
} from "legacyComponents/LineItemContainer.types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import type { AvailablePurchaseOrdersResponse } from "@btnet/services-btnet/handlers/models/availablePurchaseOrdersResponse";
import type { DueDateAssignment } from "@btnet/services-btnet/handlers/models/dueDateAssignment";
import type { DuplicateBillItems } from "@btnet/services-btnet/handlers/models/duplicateBillItems";
import type { Int32BTServiceListItem } from "@btnet/services-btnet/handlers/models/int32BTServiceListItem";
import type { IntegrationTypes } from "@btnet/services-btnet/handlers/models/integrationTypes";

import {
    type AssignedUserExtraData,
    BTSelectItem,
    type IBaseEntity,
    type IValidator,
    type IWithServiceValidationErrors,
    type ServiceValidation,
} from "types/apiResponse/apiResponse";
import {
    BilledStatus,
    type BillSyncOption,
    type CostTypes,
    CustomFieldAssociatedType,
    DocumentInstanceType,
    MarkedAs,
    type PerformingUserType,
} from "types/enum";
import { AccountingIntegrationType } from "types/enum";

import { getReadOnly } from "utilities/filters/filter";
import { calculateBuilderCost } from "utilities/lineItem/lineItemHelper";
import { calculateRetainagePercentFromWithheld } from "utilities/lineItem/retainageUtility";
import { isNullOrUndefined } from "utilities/object/object";
import { type IRelatedItem } from "utilities/relatedItem/relatedItem";

import {
    type AccountingValidationData,
    type IHasAccountingValidation,
} from "commonComponents/accounting/AccountingValidation/AccountingValidation.types";
import {
    type AddApprovalRequest,
    ApprovalInfo,
    type ApprovalStatus,
    type IApprovalInfo,
    type IApprovalServerResponse,
    type IApprovalUserRequest,
} from "commonComponents/approvals/ApprovalViewer/ApprovalViewer.types";
import { CKEditorResponse } from "commonComponents/btWrappers/BTCKEditor/BTCKEditor";
import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { GlobalUserSelectMapping } from "commonComponents/entity/globalUserSelector/GlobalUserSelector/GlobalUserSelector";
import {
    type IHasLinkedScheduleItem,
    LinkToScheduleItemEntity,
    type LinkToScheduleItemFormValues,
} from "commonComponents/entity/linkToScheduleItem/LinkToScheduleItem/LinkToScheduleItem.api.types";
import { MerchantPaymentListData } from "commonComponents/entity/merchantPayment/MerchantPaymentList/MerchantPaymentList";
import { RecentItemsTooltipConfig } from "commonComponents/entity/recentItemsTooltip/RecentItemsTooltip";
import { getVarianceForBills } from "commonComponents/entity/variance/variance.utils";
import {
    type IHasVariance,
    type IVarianceCodeExtraData,
    type IVarianceEntity,
    type VarianceFormValues,
} from "commonComponents/entity/variance/Variance/Variance.api.types";
import { type IPayOnlinePaymentInfo } from "commonComponents/financial/Checkout/Checkout.api.types";
import {
    AccountingEntityTypes,
    AccountingValidationResponse,
} from "commonComponents/financial/Common/Accounting.types";
import { type IOutboundPaymentIntegrationInformation } from "commonComponents/financial/PaymentsOverview/PaymentsOverview.api.types";
import {
    BillForVoiding,
    type ICanVoidPayment,
    type PaymentVoidConfirmFormActions,
} from "commonComponents/financial/PaymentVoidConfirm/PaymentVoidConfirm";
import { type IRelatedOwnerInvoiceProps } from "commonComponents/utilities/LineItemContainer/cellImplementations/RelatedOwnerInvoiceCell";
import { type ILineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import { type ICostLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import {
    BillPaymentForList,
    type IBillPaymentForListData,
} from "entity/bill/Bill/BillPaymentHistory/BillPaymentHistory";
import { type ISuggestedPageRange } from "entity/bill/Bill/SplitReview/splitSuggestionGroups.utility";
import { type IBillLineItem } from "entity/bill/BillLineItemContainer/BillLineItemContainer.types";
import { type AccountingBilledStatusActions } from "entity/bill/common/AccountingBilledStatus/AccountingBilledStatus";
import { type BillSourceType } from "entity/bill/common/BillSourceTag/BillSourceTag";
import {
    type IHasLienWaiver,
    type ILienWaiverFormValues,
    type ILienWaiverStatusEntityApi,
    LienWaiverEntity,
    type LienWaiverFormActions,
    type LienWaiverFormItem,
    LienWaiverStatusEntity,
    type LienWaiverStatusTypes,
    type LienWaiverType,
} from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";
import { type IAccountingPayableValidationFormValues } from "entity/purchaseOrderPayment/common/AccountingPayableValidation/AccountingPayableValidation";
import { POPaymentStatus } from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";
import { type IExternalProductLink, type VendorTypes } from "entity/vendor/common/vendor.types";

import type { default as NewBillJsonExample } from "./jsonExamples/Bill.default.api.json";
import type { default as OpenBillJsonExample } from "./jsonExamples/Bill.open.api.json";
import type { default as PaidOnlineBillJsonExample } from "./jsonExamples/Bill.paidOnline.api.json";

export interface IBillAccountingLinkInformation {
    isBillLinked: boolean;
    lastSyncDateUtc: string | null;
    integrationType: IntegrationTypes | undefined;
}

export interface IBillOcrLineItemDto {
    title?: string | null;
    description?: string | null;
    quantity?: number | null;
    unitCost?: number;
    costCode?: number;
    builderCost?: number | null;
    unitType?: string | null;
}

export interface IBillOcrSnapshotResponse {
    autofillSnapshotId?: string | null;
    summaryLineItem?: IBillOcrLineItemDto;
    lineItems?: IBillOcrLineItemDto[] | null;
    predictedCostCodeId: number | null;
    suggestedCostCodeIds: number[] | null;
    fileDownloadLink?: string | null;
    processingError?: string | null;
    dueDateAssignment?: DueDateAssignment | null;
}

export type BillFormActions =
    | "save"
    | "saveAndNew"
    | "saveAndClose"
    | "void"
    | "delete"
    | "newInvoice"
    | "refreshLienWaiverPdf"
    | AccountingBilledStatusActions
    | "payOnline"
    | "recordPayment"
    | LienWaiverFormActions
    | PaymentVoidConfirmFormActions
    | "importVariance"
    | "sendToAccounting"
    | "pay"
    | "saveToJob"
    | "markReadyForPayment"
    | "undoReadyForPayment"
    | "syncUpdatesToAccounting"
    | "approveBill"
    | "sendForApproval"
    | "rejectBill"
    | "resendForApproval"
    | "saveDraftForComment"
    | "releaseRetainage";

export interface IBillFormValues
    extends IAccountingPayableValidationFormValues,
        ILienWaiverFormValues {
    billNumber: string;
    billTitle: string;
    paymentDate?: moment.Moment;
    invoiceDate?: moment.Moment;
    performingUserId: number;
    performingUserType: PerformingUserType;
    performingUserName: string;
    performingUserEmail: string;
    miscPaidToName: string | undefined;
    unifiedDeadlineRequest: LinkToScheduleItemFormValues;
    attachedFiles: AttachedFilesRequest;
    attachedFilesConfiguration: AttachedFiles;
    lineItems: LineItem[];
    priceType: PriceTypes;
    containerIsValid: boolean;
    variance: VarianceFormValues;
    description: string;
    varianceCount: number;
    billLineItems: BillLineItem[];
    readyForPayment?: boolean;
    customFields: ICustomFieldFormValues[];
    purchaseOrderId?: number;
    baseFileHash?: string;
    baseFileId?: number;
    documentType?: BillDocumentType;
    /** Bank or credit-card account a receipt posts against. Only sent for the Receipt type. */
    paymentAccountId?: string;
    isItemizedCosts?: boolean;
    selectedApprovers?: IApprovalUserRequest[];
    resetApprovalGlobalUserIds: number[];
    approvalIdsToDelete?: number[];
    selectedJobId?: number;
    dismissBillDuplicatesWarning?: boolean;
    saveDraftToJob?: boolean;
    saveAsDraft?: boolean;
    approvalCommentJson?: string;
    approvalCommentNotificationUsers?: number[];
    approvalCommentMentionableUsers?: number[];
    isSendToAccountingDirty?: boolean;
    autofillSnapshotId?: string;
    paymentMethod?: string;
}

export interface IBillLineItemData {
    _isInEdit?: boolean;
    autoFocusedField?: string | boolean;
    costCode?: number | null;
    costCodeId?: number | null;
    costCodeTitle?: string | null;
    costCodeItemId?: number | null;
    costItemId?: number | null;
    catalogItemId?: number | null;
    costTypes?: CostTypes[] | null;
    markedAs?: MarkedAs;
    description?: string;
    id?: number;
    lineItemId?: number;
    internalNotes?: string | null;
    isDiscontinued?: boolean;
    isExpanded?: boolean;
    isSelected?: boolean;
    title?: string | null;
    itemTitle?: string | null;
    parentId?: number;
    productLink?: IExternalProductLink | null;
    quantity?: number | null;
    receiptId?: number | null;
    relatedGeneralItemId?: number | null;
    unit?: string;
    unitType?: string;
    unitCost?: number;
    varianceCode?: number;
    varianceCodeId?: number | null;
    varianceCodeTitle?: string | null;
    vendorProductId?: number;
    vendorType?: VendorTypes;
    vendorPurchaseId?: number;
    relatedOwnerInvoices?: IRelatedOwnerInvoiceProps[];
    purchaseOrderId?: number;
    purchaseOrderLineItemId?: number;
    vendorPurchaseLineItemId?: number;
    outstandingBuilderCost?: number;
    amountBilled?: number;
    isEditable?: boolean;
    builderCost?: number;
    lineItemType?: LineItemType;
    retainageWithheld?: number | null;
    retainagePercent?: number | null;
    retainageReleased?: number | null;
}

interface IBasePaymentSaveResponseData {
    id: number;
    jobId: number;
    paymentID?: number;
    formMessage?: string;
    failedFields?: ServiceValidation[];
    accountingValidation?: AccountingValidationResponse;
}

/**
 * A single selectable accounting account, as the accounting dropdowns serialize it.
 *
 * The mixed casing matches the wire: Newtonsoft's camel-case strategy applies only to properties
 * declared on `AccountingListItem` (`CanSelect`), not to those inherited from `BTListItem`.
 *
 * For the payment-account dropdown, `Value` is the bare accounting-system id (e.g. QBO's "35") —
 * a builder has one integration, so the server derives the type when it is posted back.
 */
export interface IAccountingListItem {
    Value: string;
    Text: string;
    Selected: boolean;
    canSelect: boolean;
}

/**
 * Accounting dropdown payload. `currentIdIsNotInList` means the saved selection no longer
 * exists in the connected accounting company, so the UI warns instead of silently blanking.
 */
export interface IAccountingSetupDropdownData {
    currentIdIsNotInList: boolean;
    listItems: IAccountingListItem[];
}

interface IBillForVoidingData {
    jobId: number;
    billId: number;
    purchaseOrderId: number | null;
    billNumber: string;
    status: number;
    checkNumber: string | null;
    documentType?: BillDocumentType;
}

interface IBTSelectItemData {
    id: number;
    name: string;
    secondaryName?: string | null;
    selected?: boolean;
    extraData?: unknown;
}

interface IPayOnlineResponseData {
    confirmMessage?: string;
    creditCardNickname?: string;
    checkNickname?: string;
    fees?: [{ label: string; fee: string }];
    amount?: number;
    failureMessage?: string;
}

const getLineItemContainerData = (
    data: BillEntityDataType,
    variance: IVarianceEntity,
): ILineItemContainerData => {
    return {
        jobId: data.jobId,
        pageType: "bill",
        pageTypeEnum: LineItemType.Bill,
        priceType: data.isFlatFee?.value ? PriceTypes.FlatFee : PriceTypes.LineItems,
        lineItemReadOnly: getReadOnly(data.lineItems.validators as unknown as IValidator[]),
        hideRadioButtons: false,
        showCatalog: true,
        displayTitle: true,
        canReorderLineItems: true,
        customerVarianceCodeIds: variance.varianceCodes
            .filter(
                (code: BTSelectItem<IVarianceCodeExtraData>) =>
                    code.extraData && code.extraData.isCustomerVariance,
            )
            .map((code: BTSelectItem<IVarianceCodeExtraData>) => Number(code.id)),
        lineItemsSentToAccounting: data.lineItemsSentToAccounting,
        lineItemsWithVoidedPOPLI: data.lineItemsWithVoidedPOPLI,
        lineItemsWithPayments: data.lineItemsWithPayments,
        canEditCostCodes: data.userPermissions?.canEditCostCodes,
        descriptionMax: 2500,
        hasPriceViewingPermission: data.userPermissions?.canViewPrice,
        supportsBillInvoicing: data.supportsBillInvoicing,
        showLineItemOnFlatFee: true,
        showToOwnerCheckBox: false,
        linkedCostItemType: LinkedCostItemTypes.PurchaseOrder,
        hasEditPermission: data.userPermissions?.canEdit,
        entityPending: !getReadOnly(data.lineItems.validators as unknown as IValidator[]),
        inactiveCostCodeCutoffDate: data.inactiveCostCodeCutoffDate ?? undefined,
        defaultCostCodeId: data.lineItems.defaults.costCode
            ? data.lineItems.defaults.costCode.value
            : -1,
    };
};

export enum BillLineItemColumns {
    TitleAndCostCode = 1,
    Variance = 2,
    CostTypes = 3,
    UnitCost = 4,
    Quantity = 5,
    Unit = 6,
    BuilderCost = 7,
    OwnerInvoice = 8,
    RetainagePercent = 9,
    RetainageWithheld = 10,
    Delete = 11,
}

export class BillLineItem implements ILineItemRow, ICostLineItem {
    constructor(data: IBillLineItemData) {
        this._isInEdit = data._isInEdit;
        this.autoFocusedField = data.autoFocusedField as string | undefined;
        this.costCodeId = data.costCode ?? data.costCodeId ?? null;
        this.costCodeTitle = data.costCodeTitle ?? null;
        this.costItemId = data.costCodeItemId ?? data.costItemId ?? data.catalogItemId ?? null;
        this.costTypes = data.costTypes ?? [];
        this.markedAs = data.markedAs ?? MarkedAs.None;
        this.description = data.description ?? "";
        this.id = data.id ?? data.lineItemId ?? 0;
        this.internalNotes = data.internalNotes ?? null;
        this.isDiscontinued = data.isDiscontinued;
        this.isEditable = true;
        this.isExpanded = data.isExpanded ?? false;
        this.isSelected = data.isSelected ?? false;
        this.itemTitle = data.title ?? data.itemTitle ?? null;
        this.lineItemType = LineItemType.Bill;
        this.parentId = data.parentId;
        this.productLink = data.productLink ?? undefined;
        this.quantity = Number(data.quantity);
        this.receiptId = data.receiptId ?? undefined;
        this.relatedGeneralItemId = data.relatedGeneralItemId;
        this.unit = data.unit ?? data.unitType ?? "";
        this.unitCost = Number(data.unitCost);
        this.varianceCodeId = data.varianceCode ?? 0;
        this.varianceCodeTitle = data.varianceCodeTitle ?? undefined;
        this.vendorProductId = data.vendorProductId;
        this.vendorType = data.vendorType;
        this.vendorPurchaseId = data.vendorPurchaseId;
        this.relatedOwnerInvoices = data.relatedOwnerInvoices;
        this.purchaseOrderId = data.purchaseOrderId;
        this.purchaseOrderLineItemId = data.purchaseOrderLineItemId;
        this.vendorPurchaseLineItemId = data.vendorPurchaseLineItemId;
        this.builderCost = calculateBuilderCost(this.unitCost, this.quantity);
        this.outstandingBuilderCost = data.outstandingBuilderCost;
        this.amountBilledForPO = data.amountBilled;
        this.retainageWithheld =
            data.retainageWithheld === null || data.retainageWithheld === undefined
                ? null
                : Number(data.retainageWithheld);
        this.retainagePercent =
            data.retainagePercent ??
            calculateRetainagePercentFromWithheld(this.retainageWithheld, this.builderCost);
        this.retainageReleased =
            data.retainageReleased === null || data.retainageReleased === undefined
                ? null
                : Number(data.retainageReleased);
    }

    _isInEdit?: boolean | undefined;
    autoFocusedField?: string | undefined;
    builderCost: number;
    /** Linked PO line item's outstanding amount */
    outstandingBuilderCost?: number;
    costCodeId: number | null;
    costCodeTitle: string | null;
    costItemId: number | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    description: string;
    id: number;
    internalNotes: string | null;
    isDiscontinued?: boolean | undefined;
    isEditable: boolean;
    isExpanded?: boolean | undefined;
    isSelected?: boolean | undefined;
    itemTitle: string | null;
    lineItemType: LineItemType;
    parentId?: number | undefined;
    productLink?: IExternalProductLink | undefined;
    quantity: number;
    receiptId?: number;
    relatedGeneralItemId?: number | null | undefined;
    unit: string;
    unitCost: number;
    varianceCodeId: number;
    varianceCodeTitle?: string | undefined;
    vendorProductId?: number | undefined;
    vendorType?: VendorTypes | undefined;
    vendorPurchaseId?: number;
    relatedOwnerInvoices: IRelatedOwnerInvoiceProps[] | undefined;
    purchaseOrderId: number | undefined;
    purchaseOrderLineItemId: number | undefined;
    vendorPurchaseLineItemId?: number;
    amountBilledForPO?: number;
    retainageWithheld: number | null;
    retainagePercent: number | null;
    retainageReleased: number | null;
}

export class BasePaymentSaveResponse implements IWithServiceValidationErrors {
    constructor(data: IBasePaymentSaveResponseData) {
        this.id = data.id;
        this.jobId = data.jobId;
        this.paymentId = data.paymentID || data.id;
        this.formMessage = data.formMessage ?? "";
        this.failedFields = data.failedFields!;

        if (data.accountingValidation) {
            this.failedAccountingValidation = new AccountingValidationResponse(
                data.accountingValidation,
            );
        }
    }

    id: number;
    jobId: number;
    paymentId: number;
    formMessage: string;
    failedFields: ServiceValidation[];
    failedAccountingValidation: AccountingValidationResponse;
}

export type BillEntityDataType =
    | typeof NewBillJsonExample
    | typeof OpenBillJsonExample
    | typeof PaidOnlineBillJsonExample;

export interface IAvailablePurchaseOrdersExtraData {
    performingUserId: number;
    isVariance: boolean;
    varianceCode: number | null;
    relatedPOs: number | null;
    relatedCO: number | null;
    purchaseOrderTotal: string;
}

export class AvailablePurchaseOrders {
    constructor(data: AvailablePurchaseOrdersResponse) {
        this.availablePurchaseOrders =
            data.availablePurchaseOrders?.map(
                (po: Int32BTServiceListItem) =>
                    new BTSelectItem<IAvailablePurchaseOrdersExtraData>(po),
            ) ?? [];
    }
    availablePurchaseOrders: BTSelectItem<IAvailablePurchaseOrdersExtraData>[];
}

export class BillEntity
    extends BasePaymentSaveResponse
    implements
        IBaseEntity,
        IHasLienWaiver,
        IHasLinkedScheduleItem,
        IHasAttachedFiles,
        IHasDiscussionContainer,
        IHasLineItems,
        ICanVoidPayment,
        IHasAccountingValidation,
        IHasVariance,
        IHasCustomFieldContainer
{
    constructor(data: BillEntityDataType) {
        super(data as unknown as IBasePaymentSaveResponseData);

        this.id = data.id;
        this.jobId = data.jobId;
        this.builderId = data.builderId;
        this.parentBillId = data.parentBillId ?? undefined;
        this.parentJobId = data.parentJobId ?? undefined;
        this.externalId = data.externalId ?? undefined;
        this.externalBuilderId = data.externalBuilderId ?? undefined;
        this.externalJobId = data.externalJobId ?? undefined;
        this.instanceKey = uuidv4();
        this.canAdd = data.userPermissions?.canAdd ?? false;
        this.canEdit = data.userPermissions?.canEdit ?? false;
        this.canDelete = data.canDeleteBill;
        this.canVoid = data.userPermissions?.canVoid ?? false;
        this.canCreateInvoices = data.userPermissions?.canCreateInvoices ?? false;
        this.canCreateSub = data.userPermissions?.canCreateSub ?? false;
        this.canEditJobAccess = data.userPermissions?.canEditJobAccess ?? false;
        this.canPromote = data.userPermissions?.canPromote ?? false;
        this.hasExistingInvoices = data.userPermissions?.hasExistingInvoices ?? false;
        this.canViewPrice = data.userPermissions?.canViewPrice ?? false;
        this.jobName = (data.jobInfo as { name: string } | null)?.name ?? "";
        this.createdOn = moment(data.createdOn as moment.MomentInput);
        this.createdBy = data.createdBy;
        this.createdByName = data.createdByName;
        this.createdById = data.createdById;
        this.lastUpdatedByDate = data.lastUpdatedByDate
            ? moment(data.lastUpdatedByDate as moment.MomentInput)
            : null;
        this.lastUpdatedById = data.lastUpdatedById;
        this.lastUpdatedByName = data.lastUpdatedByName ?? "";
        this.billNumberPrefix = data.billNumber.prefix;
        this.billNumber = data.billNumber.value;
        this.recentItemsTooltip = new RecentItemsTooltipConfig(data.recentItemsTooltip);
        this.billTitle = data.billTitle.value;
        this.status = data.billStatus.status;
        this.statusUpdatedByName =
            (data.billStatus as { statusUpdatedByName?: string | null }).statusUpdatedByName ??
            undefined;
        const statusUpdatedOnDate = (data.billStatus as { statusUpdatedOnDate?: string | null })
            .statusUpdatedOnDate;
        this.statusUpdatedOnDate = statusUpdatedOnDate
            ? moment(statusUpdatedOnDate as moment.MomentInput)
            : undefined;
        this.linkedPurchaseOrders = data.linkedPurchaseOrders;
        this.paymentDate = data.paymentDate.value
            ? moment(data.paymentDate.value as moment.MomentInput)
            : undefined;

        this.subRequestedNotes = data.subRequestedInfo?.notes ?? "None";
        this.subRequestedAmount = data.subRequestedInfo?.amount;
        this.subRequestedDate = data.subRequestedInfo?.date
            ? moment(data.subRequestedInfo.date as moment.MomentInput)
            : undefined;
        this.isSubRequested = data.subRequestedInfo?.isSubRequested ?? false;
        this.subRequestedInfoMessage = data.subRequestedInfo?.subRequestedInfoMessage ?? undefined;

        this.description = new CKEditorResponse(data.description);
        this.invoiceDate = data.invoiceDate.value
            ? moment(data.invoiceDate.value as moment.MomentInput)
            : undefined;
        const remappedDeadlineInfoData = {
            isDeadlineLinked: data.deadlineInfo.isDeadlineLinked,
            deadlineLinked: data.deadlineInfo.deadlineLinked,
            deadLineBeforeOrAfter: data.deadlineInfo.deadlineBeforeOrAfter,
            deadLineOffset: data.deadlineInfo.deadlineOffset,
            deadLine: data.deadlineInfo.deadline,
            dueTime: data.deadlineInfo.dueTime,
            canViewSchedules: data.deadlineInfo.canViewSchedules,
            canAddSchedules: data.deadlineInfo.canAddSchedules,
            canEditSchedules: data.deadlineInfo.canEditSchedules,
        };
        this.linkToScheduleItemEntity = new LinkToScheduleItemEntity(remappedDeadlineInfoData);
        this.attachedFiles = new AttachedFiles(data.attachedFiles);
        this.baseFileHash = data.baseFileHash ?? undefined;
        this.baseFileId = data.baseFileId ?? undefined;
        this.autofillFileDownloadLink = data.autofillFileDownloadLink ?? undefined;
        this.documentType = data.documentType ?? undefined;
        this.paymentAccounts = data.paymentAccounts ?? undefined;
        this.paymentAccountLookupFailed = data.paymentAccountLookupFailed ?? false;
        this.routesToAccountingAsExpense = data.routesToAccountingAsExpense ?? false;
        this.supportsReceiptsAsExpenses = data.supportsReceiptsAsExpenses ?? false;
        this.ocrSnapshot = data.ocrSnapshot ?? undefined;
        this.splitSuggestion =
            (data as { splitSuggestion?: ISuggestedPageRange[] | null }).splitSuggestion ??
            undefined;
        this.isSplitSuggestionDismissed =
            (data as { isSplitSuggestionDismissed?: boolean }).isSplitSuggestionDismissed ?? false;
        this.name = "Bill";
        this.documentInstanceType = DocumentInstanceType.Bill;
        this.discussions = new DiscussionsEntity(data.discussions);
        this.assignedTo = data.assignedTo.options.map((user) =>
            GlobalUserSelectMapping(
                user as unknown as BTSelectItem<AssignedUserExtraData>,
                data.assignedTo.value[0] as number | undefined,
            ),
        );
        this.miscPaidToName = data.miscPaidToName?.value ?? undefined;
        this.paidToEmail = data.paidToEmail ?? undefined;
        this.variance = getVarianceForBills(data as Parameters<typeof getVarianceForBills>[0]);

        this.lineItems = data.lineItems as unknown as ILineItemData;
        this.lineItemContainerData = getLineItemContainerData(data, this.variance);
        this.lineItemInitialValues = data.lineItems.value
            ? (data.lineItems.value as unknown as ILineItemResponse[]).map(
                  (x) =>
                      new LineItem(
                          x,
                          this.lineItemContainerData.pageType,
                          this.lineItemContainerData.pageTypeEnum,
                      ),
              )
            : [];
        this.merchantPayments = data.merchantPayments
            ? new MerchantPaymentListData(data.merchantPayments)
            : undefined;
        this.payments = data.payments
            ? data.payments.map((x: IBillPaymentForListData) => new BillPaymentForList(x))
            : [];
        this.deleteMessage = data.deleteButton.buttonConfirmationMessage;
        this.lienWaiverStatus = data.lienWaiverInfo
            ? new LienWaiverStatusEntity(
                  data.lienWaiverInfo as unknown as ILienWaiverStatusEntityApi,
              )
            : undefined;

        this.voidTitle = data.voidButton.voidTitle;
        this.voidMessage = data.voidButton.voidMessage;
        this.voidCheckboxMessage = data.voidButton.voidCheckboxMessage ?? undefined;
        this.voidTooltip = data.voidButton.voidTooltip ?? undefined;
        this.additionalVoidAlert = data.voidButton.additionalVoidAlert ?? undefined;
        const billsTiedToSamePayment = data.voidButton.billsTiedToSamePayment as unknown as
            | IBillForVoidingData[]
            | null;
        if (billsTiedToSamePayment) {
            this.billsTiedToSamePayment = billsTiedToSamePayment.map((x) => new BillForVoiding(x));
            this.billsBeingVoided = [
                {
                    billId: this.id,
                    jobId: this.jobId,
                    billNumber: this.billTitle,
                    status: this.status,
                    purchaseOrderId: null,
                    checkNumber: null,
                },
            ];
        }

        this.paymentMethodName = data.paymentMethodName;
        this.accountingType = data.accountingInfo?.accountingType ?? AccountingIntegrationType.None;
        this.isConnectedToAccounting = this.accountingType !== AccountingIntegrationType.None;
        this.isBilled = data.isBilled || false;
        this.billedStatus = data.accountingInfo?.billedStatusValue ?? BilledStatus.Unknown;
        this.canRefreshBilledStatus = data.accountingInfo?.canRefreshBilledStatus ?? false;
        this.syncToAccountingOverride = data.accountingInfo?.syncToAccountingOverride ?? null;
        this.hasDeletableBills = data.accountingInfo?.hasDeletableBills ?? false;
        this.externalAccountingLink = data.accountingInfo?.externalAccountingLink ?? undefined;
        this.lastAccountingSyncDateText =
            data.accountingInfo?.lastAccountingSyncDateText ?? undefined;
        this.allowAccountingBillCreation =
            data.accountingInfo?.allowAccountingBillCreation ?? false;

        const allowBillToOwnerSetting = !isNullOrUndefined(
            data.accountingInfo?.billToOwnerCheckbox,
        );
        const allowSendToAccountingSetting = !isNullOrUndefined(
            data.accountingInfo?.sendToAccountingCheckbox,
        );
        this.canResyncToAccounting = data.accountingInfo?.canResyncToAccounting ?? true;
        this.resyncToAccountingBannerText =
            data.accountingInfo?.resyncToAccountingBannerText ?? undefined;
        this.resyncToAccountingMessageText =
            data.accountingInfo?.resyncToAccountingMessageText ?? undefined;
        this.canShowValidationSummary = data.accountingInfo?.canShowValidationSummary ?? false;
        this.hasPendingBillEdits = data.accountingInfo?.hasPendingBillEdits ?? false;

        if (allowBillToOwnerSetting) {
            this.billToOwner = (
                data.accountingInfo?.billToOwnerCheckbox as { value: boolean } | null
            )?.value;
        }

        if (allowSendToAccountingSetting) {
            this.sendToAccountingDefault =
                (data.accountingInfo?.sendToAccountingCheckbox as { value: boolean } | null)
                    ?.value && this.allowAccountingBillCreation;
        }
        this.accountingValidation = {
            entityId: this.id || 0,
            entityType: AccountingEntityTypes.Bill,
            showValidation: true,
            showAccountsPayable: true,
            canGetLatestStatus: true,
            canResetAccounting: false,
            shouldValidateOnLoad: true,
            accountingIntegrationName: (data.accountingInfo?.accountingSectionTitle ??
                null) as string,
            canSendToAccounting: allowSendToAccountingSetting,
            canBillToOwner: allowBillToOwnerSetting,
        };
        this.billSyncOption =
            (data.accountingInfo?.billSyncOption as BillSyncOption | undefined) ?? undefined;
        this.canMarkPaid = data.userPermissions?.canMarkPaid;
        this.showPayOnlineMarketing = data.onlinePaymentInfo.showPayOnlineMarketing ?? false;
        this.showPayOnline =
            data.onlinePaymentInfo.showPayOnlineButton || this.showPayOnlineMarketing;
        this.payOnlineError = data.payOnlineError ?? "";
        this.isTaxPayable = data.isTaxPayable || false;

        this.lienWaiverId =
            (data.lienWaiverInfo?.attachedLienWaiver as { id: number } | null)?.id ?? 0;
        this.lienWaiversAllowed = data.lienWaiverInfo.lienWaiversAllowed;
        this.lienWaiverBillId = data.id;
        this.isTemplate = data.isTemplate;
        this.isBill = true;

        this.fieldsReadonly = data.fieldsReadonly;
        this.hasPendingOrCompletedMerchantPayment =
            data.hasPendingOrCompletedMerchantPayment ?? false;
        this.hasActiveScheduledPayment = data.hasActiveScheduledPayment ?? false;
        this.canChangeJob = data.canChangeJob ?? false;
        this.isSyncedToAccounting = data.isSyncedToAccounting ?? false;
        this.hasReleasedLienWaiver = data.hasReleasedLienWaiver ?? false;
        this.isOnOwnerInvoice = data.isOnOwnerInvoice ?? false;
        this.hasDocumentAccountingLinks = data.hasDocumentAccountingLinks ?? false;
        this.isSplit = data.isSplit ?? false;
        this.noVendorCreditSupportMessage = data.warningMsg ?? undefined;
        this.billLineItems =
            (data.lineItems?.value as unknown as IBillLineItemData[])?.map(
                (x) => new BillLineItem(x),
            ) ?? [];
        this.retainageHeldNet = this.billLineItems.reduce(
            (sum, li) => sum + (li.retainageWithheld ?? 0) - (li.retainageReleased ?? 0),
            0,
        );

        this.canPayWithECheck = data.canPayWithECheck;
        this.builderCountry = data.builderCountry;
        this.onlinePaymentInfo = data.onlinePaymentInfo?.paymentInfo ?? undefined;
        this.isReadyForPaymentVisible =
            !!data.readyForPayment &&
            (data.billStatus.status as POPaymentStatus) === POPaymentStatus.Open;

        this.attachedLienWaiver = data.lienWaiverInfo.attachedLienWaiver
            ? new LienWaiverEntity(data.lienWaiverInfo.attachedLienWaiver)
            : undefined;
        this.attachedLienWaivers = data.lienWaiverInfo.attachedLienWaivers?.map(
            (lw) => new LienWaiverEntity(lw),
        );
        this.lienWaiverForms =
            data.lienWaiverInfo?.lienWaiverForms?.map(
                (lwf: IBTSelectItemData): LienWaiverFormItem => new BTSelectItem(lwf),
            ) ?? undefined;

        const customFieldInfo = data.customFieldInfo as typeof data.customFieldInfo | null;
        this.customFieldAssociatedType =
            customFieldInfo?.customFieldAssociatedType ??
            CustomFieldAssociatedType.PurchaseOrderPayments;
        this.customFieldOptions = customFieldInfo?.customFieldOptions ?? {};
        this.customFields = (customFieldInfo?.customFields ??
            []) as unknown as ICustomFieldResponse[];
        this.customFieldsCanConfigure = customFieldInfo?.customFieldsCanConfigure ?? false;
        this.duplicateBillInfo = data.duplicateBillInfo ?? undefined;
        this.hasCostCodesFeature = data.hasCostCodesFeature;
        this.hasInactiveCostCodes = data.hasInactiveCostCodes;
        this.hasInactiveCostCodesUserWarning = data.hasInactiveCostCodesUserWarning ?? "";
        this.outboundPaymentIntegrationInformation = data.outboundPaymentIntegrationInformation;
        this.hasSendPONumberWithBill = data.hasSendPONumberWithBill;
        this.availablePurchaseOrders = new AvailablePurchaseOrders(
            data as AvailablePurchaseOrdersResponse,
        ).availablePurchaseOrders;
        this.allowBillEditSyncs = data.allowBillEditSyncs;
        this.retainageEnabledForJob = data.retainageEnabledForJob ?? false;
        this.defaultBillRetainagePercent =
            data.defaultBillRetainagePercent === null ||
            data.defaultBillRetainagePercent === undefined
                ? null
                : Number(data.defaultBillRetainagePercent);
        this.apRetainageProductServiceId =
            (data as { apRetainageProductServiceId?: string | null }).apRetainageProductServiceId ??
            null;
        this.supportsApRetainage =
            (data as { supportsApRetainage?: boolean }).supportsApRetainage ?? false;
        this.billAccountingLinkInformation = data.billAccountingLinkInformation ?? undefined;

        this.approvals =
            (data.approvals as unknown[] | null)?.map(
                (approval: unknown) => new ApprovalInfo(approval as IApprovalServerResponse),
            ) ?? [];
        this.currentApprovalStep = data.currentApprovalStep ?? null;
        this.billSource = (data as { billSource?: BillSourceType | null }).billSource ?? null;
        this.lastChanged = (data as { lastChanged?: string | null }).lastChanged ?? undefined;
        this.processingError =
            (data.ocrSnapshot as IBillOcrSnapshotResponse | null | undefined)?.processingError ??
            null;
        this.amountPaid = data.amountPaid ?? 0;
        this.remainingBalance = data.remainingBalance ?? 0;
    }

    id: number;
    popId: number;
    jobId: number;
    builderId: number;
    /** Set when this bill is itself a split child ("cost record") - the id/jobId of its parent
     * bill. Undefined for a root bill. Used to redirect a direct load of a child bill (from Job
     * Costing Budget, lists, deep links, etc.) to the parent bill + Cost Record modal instead of
     * rendering it as a standard bill (ado-288925). */
    parentBillId: number | undefined;
    parentJobId: number | undefined;
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    instanceKey: string;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canVoid: boolean;
    canCreateInvoices: boolean;
    canCreateSub: boolean;
    canEditJobAccess: boolean;
    canPromote: boolean;
    hasExistingInvoices: boolean;
    canViewPrice: boolean;
    jobName: string;
    createdOn: moment.Moment;
    createdBy: string;
    createdByName: string;
    createdById: number | null;
    lastUpdatedByName: string;
    lastUpdatedById: number | null;
    lastUpdatedByDate: moment.Moment | null;
    billNumberPrefix: string;
    billNumber: string;
    recentItemsTooltip: RecentItemsTooltipConfig;
    billTitle: string;
    status: POPaymentStatus;
    paymentDate?: moment.Moment;
    subRequestedAmount?: number;
    subRequestedNotes: string;
    subRequestedDate?: moment.Moment;
    description: CKEditorResponse;
    invoiceDate?: moment.Moment;
    linkToScheduleItemEntity: LinkToScheduleItemEntity;
    attachedFiles: AttachedFiles;
    name: string;
    documentInstanceType: DocumentInstanceType;
    discussions: DiscussionsEntity;
    assignedTo: BTSelectItem<AssignedUserExtraData>[];
    miscPaidToName: string | undefined;
    paidToEmail: string | undefined;
    lineItems: ILineItemData;
    lineItemContainerData: ILineItemContainerData;
    lineItemInitialValues: LineItem[];
    merchantPayments: MerchantPaymentListData | undefined;
    payments: BillPaymentForList[];
    deleteMessage: string;
    billedStatus: BilledStatus;
    voidTitle: string;
    voidMessage: string;
    voidTooltip?: string;
    voidCheckboxMessage?: string;
    additionalVoidAlert?: string;
    variance: IVarianceEntity;
    billsTiedToSamePayment?: BillForVoiding[];
    billsBeingVoided?: BillForVoiding[];
    lienWaiverStatus?: LienWaiverStatusEntity;
    paymentMethodName: string | null;
    isConnectedToAccounting: boolean;
    isBilled: boolean;
    accountingType: AccountingIntegrationType;
    canRefreshBilledStatus: boolean;
    syncToAccountingOverride?: boolean | null;
    hasDeletableBills: boolean;
    canResyncToAccounting: boolean;
    resyncToAccountingBannerText: string | undefined;
    resyncToAccountingMessageText: string | undefined;
    hasPendingBillEdits: boolean;
    canShowValidationSummary: boolean;
    accountingValidation: AccountingValidationData;
    billSyncOption?: BillSyncOption;
    canSendToAccounting: boolean;
    billToOwner?: boolean;
    sendToAccountingDefault?: boolean;
    allowAccountingBillCreation: boolean;
    canMarkPaid: boolean;
    showPayOnline?: boolean;
    showPayOnlineMarketing: boolean;
    payOnlineError: string;
    isTaxPayable: boolean;
    lienWaiverId: number;
    lienWaiversAllowed: boolean;
    isTemplate: boolean;
    isBill: boolean;
    fieldsReadonly: boolean;
    hasPendingOrCompletedMerchantPayment: boolean;
    hasActiveScheduledPayment: boolean;
    canChangeJob: boolean;
    isSyncedToAccounting: boolean;
    hasReleasedLienWaiver: boolean;
    isOnOwnerInvoice: boolean;
    hasDocumentAccountingLinks: boolean;
    /** True when this bill has been split into per-job child bills (it has child bills). */
    isSplit: boolean;
    noVendorCreditSupportMessage: string | undefined;
    billLineItems: BillLineItem[];
    /** Sum(retainageWithheld) - sum(retainageReleased) across billLineItems. */
    retainageHeldNet: number;
    canPayWithECheck: boolean;
    builderCountry: string;
    onlinePaymentInfo?: IPayOnlinePaymentInfo;
    isReadyForPaymentVisible: boolean;
    linkedPurchaseOrders: { [key: number]: BillRelatedPurchaseOrderItem };
    availablePurchaseOrders: BTSelectItem<IAvailablePurchaseOrdersExtraData>[];
    allowBillEditSyncs: boolean;
    retainageEnabledForJob: boolean;
    defaultBillRetainagePercent: number | null;
    apRetainageProductServiceId: string | null;
    supportsApRetainage: boolean;
    billAccountingLinkInformation?: IBillAccountingLinkInformation;

    // IHasLienWaiver
    lienWaiverBillId: number;
    attachedLienWaiver?: LienWaiverEntity;
    attachedLienWaivers?: LienWaiverEntity[];
    lienWaiverForms: LienWaiverFormItem[];

    // IHasCustomFieldContainer
    customFieldAssociatedType: CustomFieldAssociatedType;
    customFieldOptions: ICustomFieldOptionsList;
    customFields: ICustomFieldResponse[];
    customFieldsCanConfigure: boolean;
    duplicateBillInfo?: DuplicateBillItems[];

    hasCostCodesFeature: boolean;
    hasInactiveCostCodes: boolean;
    hasInactiveCostCodesUserWarning: string;

    outboundPaymentIntegrationInformation: IOutboundPaymentIntegrationInformation;

    statusUpdatedByName?: string;
    statusUpdatedOnDate?: moment.Moment;

    approvals: IApprovalInfo[];
    currentApprovalStep: number | null;

    externalAccountingLink?: string;
    lastAccountingSyncDateText?: string;
    hasSendPONumberWithBill: boolean;
    isSubRequested: boolean;
    subRequestedInfoMessage?: string;
    billSource?: BillSourceType | null;
    lastChanged?: string | null;
    processingError: string | null;
    baseFileHash?: string;
    baseFileId?: number;
    autofillFileDownloadLink?: string | null;
    documentType?: BillDocumentType;
    paymentAccounts?: IAccountingSetupDropdownData;
    /**
     * An account category could not be read, so `paymentAccounts` is incomplete rather than short.
     * `paymentAccountId` is then left off the save request: the empty selection the picker shows is
     * not the user clearing the account.
     */
    paymentAccountLookupFailed: boolean;
    /** Always false on an unsaved bill, whose type is chosen after load; see `getIsExpense`. */
    routesToAccountingAsExpense: boolean;
    /** The builder's integration capability, independent of this document's type. */
    supportsReceiptsAsExpenses: boolean;
    ocrSnapshot?: IBillOcrSnapshotResponse;
    splitSuggestion?: ISuggestedPageRange[] | null;
    /** Whether the banner announcing `splitSuggestion` has been dismissed for this document. The
     * ranges stay available to the split review either way. */
    isSplitSuggestionDismissed?: boolean;
    amountPaid: number;
    remainingBalance: number;
}

export class BillDeleteResponse {}
export class BillVoidResponse {}

export interface IBillSaveLienWaiverInfo {
    lienWaiverId: number;
    lienWaiverFormId: number;
    lienWaiverText: string;
    isMakingPayment: boolean;
    sendLienWaiverWithPayment: boolean;
    lienWaiverType: LienWaiverType;
    lienWaiverStatus: LienWaiverStatusTypes;
    savePdf: boolean;
    pdfTitle: string;
    updateCostItems: boolean;
    attachedFiles?: AttachedFilesRequest;
}

export interface IBillSaveRequest {
    jobId: number;
    billId: number;
    /** Gates the non-draft job-change cascade; true only for a staged Change Job so a stale jobId can't silently reassign the bill. Ignored for drafts, which reassign via the inline dropdown (ado-280342). */
    isJobChange: boolean;
    billNumber: string;
    billTitle: string;
    relatedCO?: number | null;
    description: string;
    paymentDate?: moment.Moment;
    invoiceDate?: moment.Moment;
    readyForPayment: boolean;
    sendToAccounting: boolean;
    syncToAccountingOverride?: boolean | null;
    billToOwner: boolean;
    attachedFiles: AttachedFilesRequest;
    customFields: ICustomFieldFormValues[];
    lineItems: LineItem[];
    payInFull?: boolean;
    payOnline?: boolean;
    markAsPaid?: boolean;
    undoReadyForPayment?: boolean;
    assignedToInfo: {
        assignedToType: PerformingUserType;
        assignedToId: number;
        assignedToName: string;
        assignedToEmail: string;
    };
    varianceInfo: {
        varianceCount: number;
        isVariance: boolean;
        varianceCode: number;
        isEntireBillVariance: boolean;
        hasLineItemCustomerVariance: boolean;
        relatedChangeOrderId: number;
        relatedPurchaseOrderId: number;
    } | null;
    lienWaiverInfo: IBillSaveLienWaiverInfo | null;
    /** One entry per applied type; sent under the MultipleLienWaiversOnBills flag so both a
     * conditional and an unconditional persist in one save. Null on the legacy single-waiver path. */
    lienWaiverInfos: IBillSaveLienWaiverInfo[] | null;
    deadLineInfo: LinkToScheduleItemFormValues | null;
    isCreateNewFromPO: boolean;
    syncUpdatesToAccounting: boolean;
    sendForApproval?: boolean;
    resendForApproval?: boolean;
    /** @deprecated Use approvalStatus instead. Kept for backward compatibility during staged deployment. */
    approveBill?: boolean;
    saveDraftToJob?: boolean;
    saveAsDraft?: boolean;
    approvalStatus?: ApprovalStatus;
    approvers?: AddApprovalRequest[] | null;
    dismissBillDuplicatesWarning?: boolean;
    resetApprovalGlobalUserIds?: number[];
    approvalIdsToDelete?: number[];
    approvalCommentText?: string;
    approvalCommentJson?: string;
    approvalCommentNotificationUsers?: number[];
    approvalCommentMentionableUsers?: number[];
    documentType?: BillDocumentType;
    /** Selected bank or credit-card account for an expense, as an accounting list-item value. */
    paymentAccountId?: string;
    paymentMethod?: string;
}

export function transformBillLineItemsToLineItems(billLineItems: BillLineItem[]) {
    return billLineItems.map(
        (bill: IBillLineItem) =>
            new LineItem(
                {
                    id: bill.id > 0 ? bill.id : 0,
                    costCodeId: bill.costCodeId,
                    costCode: bill.costCodeId!,
                    costCodeItemId: bill.costItemId,
                    catalogItemId: bill.costItemId,
                    unitCost: bill.unitCost,
                    quantity: bill.quantity,
                    unitType: bill.unit,
                    builderCost: bill.builderCost,
                    title: bill.itemTitle,
                    description: bill.description,
                    costTypes: bill.costTypes,
                    markedAs: bill.markedAs,
                    receiptId: bill.receiptId,
                    relatedGeneralItemId: bill.relatedGeneralItemId,
                    internalNotes: bill.internalNotes,
                    vendorProductId: bill.vendorProductId,
                    vendorType: bill.vendorType,
                    vendorPurchaseId: bill.vendorPurchaseId,
                    vendorPurchaseLineItemId: bill.vendorPurchaseLineItemId,
                    varianceCode: bill.varianceCodeId,
                    purchaseOrderId: bill.purchaseOrderId,
                    purchaseOrderLineItemId: bill.purchaseOrderLineItemId,
                    retainageWithheld: bill.retainageWithheld,
                    retainageReleased: bill.retainageReleased,
                } as ILineItemResponse,
                "",
                LineItemType.Bill,
            ),
    );
}

export function transformLineItemsToBillLineItems(lineItems: LineItem[]): BillLineItem[] {
    return lineItems.map(
        (item: LineItem) => new BillLineItem(item as unknown as IBillLineItemData),
    );
}

export enum BillLockStatus {
    Unlocked = 0,
    CostCodeUnlocked = 1,
    TitleInternalNotesUnlocked = 2,
    FullyLocked = 3,
}

export enum BillDocumentType {
    Bill = 0,
    /** Money already spent. Posts to accounting as an A/P bill. */
    Receipt = 1,
    VendorCredit = 2,
    /**
     * Receipt that posts to QBO as a Purchase rather than an A/P bill. Server-owned and read-only:
     * the picker never offers it and the client never sends it. Displays as "Receipt" everywhere,
     * so use `isReceiptDocumentType` for any "is this a receipt" check.
     */
    Expense = 3,
}

/**
 * True for either receipt flavour. Comparing against `BillDocumentType.Receipt` alone silently
 * excludes {@link BillDocumentType.Expense} and makes the bill read as a plain Bill.
 */
export const isReceiptDocumentType = (documentType: BillDocumentType | undefined): boolean =>
    documentType === BillDocumentType.Receipt || documentType === BillDocumentType.Expense;

/**
 * The value the Document Type picker can actually show. Seeding form values with
 * {@link BillDocumentType.Expense} raw leaves the Type field blank, so collapse it to Receipt at
 * the form boundary. The stored flavour is untouched: the server never re-derives it from a save.
 */
export const toSelectableDocumentType = (
    documentType: BillDocumentType | undefined,
): BillDocumentType | undefined =>
    documentType === BillDocumentType.Expense ? BillDocumentType.Receipt : documentType;

export class UpdateBillingStatusResponse {}

export class DeleteBilledResponse {}

export class PayOnlineResponse {
    constructor(data: IPayOnlineResponseData) {
        this.confirmMessage = data.confirmMessage;
        this.payingWithType = data.creditCardNickname ? "Credit Card" : "Check";
        this.payingWithTitle = data.creditCardNickname || data.checkNickname || "";
        this.fee = data.fees;
        this.amount = data.amount;
        this.failureMessage = data.failureMessage;
    }
    failureMessage?: string;
    confirmMessage?: string;
    amount?: number;
    payingWithType: string;
    payingWithTitle: string;
    fee?: [{ label: string; fee: string }];
}

export class BillRelatedPurchaseOrderItem implements IRelatedItem {
    id: number;
    jobId: number;
    title: string;
    poPrefixAndNumber?: string;
    hasAmendment: boolean;
}

export interface IBillSaveOptions {
    payInFull: boolean;
    payOnline: boolean;
    /**
     * The user pressed "Mark as Paid" rather than "Mark Ready for Payment". Records no payment,
     * unlike `payInFull`/`payOnline` — an expense's money already left the account. Sent so
     * the server honors the pressed action instead of inferring it from the document type.
     */
    markAsPaid: boolean;
    /** Non-paying persist before the offline payment dialog opens; must not sync to accounting. */
    recordPayment: boolean;
    undoReadyForPayment: boolean;
    resendForApproval: boolean;
    syncUpdatesToAccounting: boolean;
    sendForApproval: boolean;
    approvalStatus?: ApprovalStatus;
    saveDraftToJob: boolean;
    isJobChange?: boolean;
}
